import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { generateId } from '@/utils/helpers';
import toast from 'react-hot-toast';


export const useChat = (gpcService, turnstileToken, onSessionExpired) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);


  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim() || isLoading) {
        return;
      }

      if (!turnstileToken) {
        toast.error('Validación anti-bot pendiente');
        return;
      }

      const userMessage = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date().toISOString()
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      const tempMessageId = generateId();
      const tempMessage = {
        id: tempMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };

      setMessages((prev) => [...prev, tempMessage]);

      try {
        const response = await gpcService.sendMessage(
          content,
          turnstileToken,
          (chunk) => {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === tempMessageId
                  ? { ...msg, content: msg.content + chunk }
                  : msg
              )
            );
          }
        );

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === tempMessageId
              ? {
                  id: response.data.message_id,
                  role: 'assistant',
                  content: response.data.response,
                  timestamp: response.data.created_at,
                  isStreaming: false,
                  metadata: {
                    costUsd: response.data.cost_usd,
                    creditBalanceUsd: response.data.credit_balance_usd,
                    inputTokens: response.data.input_tokens,
                    outputTokens: response.data.output_tokens,
                    responseTimeMs: response.data.response_time_ms,
                    modelUsed: response.data.model_used
                  }
                }
              : msg
          )
        );
      } catch (err) {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));

        let errorData;
        try {
          errorData = JSON.parse(err.message);
        } catch {
          errorData = { statusCode: 500, message: err.message };
        }

        let errorMessage;
        const statusCode = errorData.statusCode || 500;
        const errorCode = errorData.errorCode || '';

        // Handle session expiration specifically
        if (statusCode === 401 && errorCode === 'SESSION_EXPIRED') {
          errorMessage = t('errors.sessionExpired', 'Session expired, please verify again');
          if (onSessionExpired) {
            onSessionExpired();
          }
        }

        switch (statusCode) {
          case 401:
            if (!errorMessage) {
              errorMessage = t('errors.unauthorized');
            }
            break;
          case 402:
            errorMessage = t('errors.insufficientCredits');
            break;
          case 403:
            if (errorCode.includes('DOMAIN') || errorCode.includes('UNAUTHORIZED')) {
              errorMessage = t('errors.domainUnauthorized');
            } else {
              errorMessage = t('errors.validation');
            }
            break;
          case 429:
            errorMessage = t('errors.rateLimit');
            break;
          case 503:
          case 502:
          case 504:
            errorMessage = t('errors.aiServiceUnavailable');
            break;
          case 500:
            errorMessage = t('errors.serverError');
            break;
          default:
            errorMessage = t('errors.generic');
        }

        setError(errorMessage);

        toast.error(errorMessage, {
          duration: 4000,
          position: 'top-center'
        });

        const errorMsg = {
          id: generateId(),
          role: 'assistant',
          content: errorMessage,
          timestamp: new Date().toISOString()
        };

        setMessages((prev) => [...prev, errorMsg]);
      } finally {
        setIsLoading(false);
      }
    },
    [gpcService, turnstileToken, isLoading, t, onSessionExpired]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    clearError
  };
};
